/**
 * PlatformSettingsTab — Global platform branding + defaults.
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MediaUpload } from '@/components/MediaUpload';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Globe, Palette, Mail, Sparkles, Radio, Send } from 'lucide-react';
import { toast } from 'sonner';
import { superAdminApi } from './superAdminApi';

export const PlatformSettingsTab = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-platform-settings'],
    queryFn: superAdminApi.getPlatformSettings,
  });
  const [form, setForm] = useState({});
  useEffect(() => { if (data) setForm(data); }, [data]);

  const mut = useMutation({
    mutationFn: (payload) => superAdminApi.updatePlatformSettings(payload),
    onSuccess: () => {
      toast.success('Platform settings saved');
      qc.invalidateQueries({ queryKey: ['superadmin-platform-settings'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const setField = (k, v) => setForm({ ...form, [k]: v });

  if (isLoading) return <div className="text-slate-400 p-8">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="superadmin-platformsettings-tab">
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Globe className="h-4 w-4" /> Identity</CardTitle>
            <CardDescription className="text-slate-400 text-xs">How your platform presents itself publicly</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Platform Name</Label>
              <Input value={form.platform_name || ''} onChange={e => setField('platform_name', e.target.value)}
                     className="bg-white/5 border-white/10 text-white" data-testid="ps-platform-name" />
            </div>
            <div><Label>Tagline</Label>
              <Input value={form.platform_tagline || ''} onChange={e => setField('platform_tagline', e.target.value)}
                     className="bg-white/5 border-white/10 text-white" data-testid="ps-tagline" />
            </div>
            <div><Label>Marketing Tagline (Pricing page)</Label>
              <Input value={form.marketing_tagline || ''} onChange={e => setField('marketing_tagline', e.target.value)}
                     className="bg-white/5 border-white/10 text-white" />
            </div>
            <div><Label>Logo</Label>
              <MediaUpload value={form.logo_url || ''} purpose="logo"
                           testId="ps-logo-upload"
                           onChange={(url) => setField('logo_url', url)} />
            </div>
            <div><Label>Favicon</Label>
              <MediaUpload value={form.favicon_url || ''} purpose="favicon"
                           testId="ps-favicon-upload" previewClassName="h-10"
                           onChange={(url) => setField('favicon_url', url)} />
            </div>
          </CardContent>
        </Card>

        {/* Pricing page visibility + copy */}
        <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">💳 Pricing page</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.pricing_page_enabled !== false}
                onChange={e => setField('pricing_page_enabled', e.target.checked)}
                className="h-4 w-4 rounded border-slate-400"
                data-testid="ps-pricing-page-enabled"
              />
              <span className="text-sm">Show public <code className="text-indigo-300">/pricing</code> page</span>
            </label>
            <div>
              <Label>Pricing page headline</Label>
              <Input
                value={form.pricing_page_headline || ''}
                onChange={e => setField('pricing_page_headline', e.target.value)}
                placeholder="One platform for the whole institution"
                className="bg-white/5 border-white/10 text-white"
                data-testid="ps-pricing-headline"
              />
            </div>
            <div>
              <Label>Pricing page subheadline</Label>
              <Input
                value={form.pricing_page_subheadline || ''}
                onChange={e => setField('pricing_page_subheadline', e.target.value)}
                placeholder="Pick the scope that fits — or combine them all."
                className="bg-white/5 border-white/10 text-white"
                data-testid="ps-pricing-subheadline"
              />
            </div>
            <div>
              <Label>Scope tab order (comma separated)</Label>
              <Input
                value={(form.pricing_scope_order || []).join(',')}
                onChange={e => setField('pricing_scope_order',
                  e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="school,event,church,ministry"
                className="bg-white/5 border-white/10 text-white"
                data-testid="ps-pricing-scope-order"
              />
              <p className="text-[11px] text-slate-400 mt-1">Allowed: school, event, church, ministry, standalone</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Palette className="h-4 w-4" /> Branding</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Primary Color</Label>
              <div className="flex gap-2">
                <Input type="color" value={form.primary_color || '#6366f1'}
                       onChange={e => setField('primary_color', e.target.value)}
                       className="bg-white/5 border-white/10 text-white h-10 w-20" />
                <Input value={form.primary_color || '#6366f1'}
                       onChange={e => setField('primary_color', e.target.value)}
                       className="bg-white/5 border-white/10 text-white flex-1" />
              </div>
            </div>
            <div><Label>Hero image (Pricing page)</Label>
              <MediaUpload value={form.hero_image_url || ''} purpose="hero"
                           testId="ps-hero-upload"
                           onChange={(url) => setField('hero_image_url', url)} />
            </div>
            <div><Label>Terms of Service URL</Label>
              <Input value={form.terms_url || ''} onChange={e => setField('terms_url', e.target.value)}
                     className="bg-white/5 border-white/10 text-white" />
            </div>
            <div><Label>Privacy Policy URL</Label>
              <Input value={form.privacy_url || ''} onChange={e => setField('privacy_url', e.target.value)}
                     className="bg-white/5 border-white/10 text-white" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Mail className="h-4 w-4" /> Support Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Support Email</Label>
              <Input type="email" value={form.support_email || ''}
                     onChange={e => setField('support_email', e.target.value)}
                     className="bg-white/5 border-white/10 text-white" data-testid="ps-support-email" />
            </div>
            <div><Label>Support Phone</Label>
              <Input value={form.support_phone || ''} onChange={e => setField('support_phone', e.target.value)}
                     className="bg-white/5 border-white/10 text-white" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Sparkles className="h-4 w-4" /> Defaults & Policies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Default Currency</Label>
                <Select value={form.default_currency || 'USD'}
                        onValueChange={v => setField('default_currency', v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['USD', 'EUR', 'GBP', 'ZAR', 'ZWL', 'NGN', 'KES'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Default Trial Days</Label>
                <Input type="number" value={form.default_trial_days || 0}
                       onChange={e => setField('default_trial_days', parseInt(e.target.value) || 0)}
                       className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <div><Label>Default Plan Code</Label>
              <Input value={form.default_plan || 'trial'}
                     onChange={e => setField('default_plan', e.target.value)}
                     className="bg-white/5 border-white/10 text-white" />
            </div>
            <label className="flex items-center gap-2 pt-2">
              <Checkbox checked={!!form.require_application_approval}
                        onCheckedChange={v => setField('require_application_approval', !!v)}
                        data-testid="ps-require-approval" />
              <span className="text-sm">Require manual approval for new applications</span>
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={!!form.allow_public_signup}
                        onCheckedChange={v => setField('allow_public_signup', !!v)} />
              <span className="text-sm">Allow public signup (skip application workflow)</span>
            </label>
          </CardContent>
        </Card>
      </div>

      {/* Real-time Alert Integrations */}
      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white" data-testid="ps-alert-integrations-card">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2"><Radio className="h-4 w-4 text-emerald-400" /> Alert Integrations</CardTitle>
          <CardDescription className="text-slate-400 text-xs">Pipe Command Center alerts (urgent safeguarding, critical behaviour, support tickets) to Slack or Teams in real time.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2">
            <Checkbox checked={!!form.slack_alerts_enabled}
                      onCheckedChange={v => setField('slack_alerts_enabled', !!v)}
                      data-testid="ps-slack-enabled" />
            <span className="text-sm">Enable Slack / Teams webhook alerts</span>
          </label>
          <div><Label>Incoming Webhook URL</Label>
            <Input value={form.slack_webhook_url || ''}
                   onChange={e => setField('slack_webhook_url', e.target.value)}
                   placeholder="https://hooks.slack.com/services/..."
                   className="bg-white/5 border-white/10 text-white font-mono text-xs"
                   data-testid="ps-slack-webhook-url" />
            <p className="text-[11px] text-slate-500 mt-1">
              Slack: Apps → Incoming Webhooks → Add → copy the URL. Teams: Channel → Connectors → Incoming Webhook.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Minimum Severity</Label>
              <Select value={form.slack_min_severity || 'high'}
                      onValueChange={v => setField('slack_min_severity', v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="ps-slack-min-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'medium', 'high', 'critical'].map(s => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button type="button" variant="outline"
                      disabled={!form.slack_webhook_url}
                      onClick={async () => {
                        try {
                          const r = await superAdminApi.testAlertWebhook(form.slack_webhook_url);
                          if (r?.ok) toast.success('Webhook fired — check your Slack/Teams channel');
                          else toast.error('Webhook failed — check the URL');
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Webhook test failed'); }
                      }}
                      className="text-white border-white/20 bg-white/5 hover:bg-white/10"
                      data-testid="ps-slack-test-btn">
                <Send className="h-4 w-4 mr-2" /> Send test
              </Button>
              <Button type="button" variant="outline"
                      onClick={async () => {
                        try {
                          await superAdminApi.previewAdminAlert();
                          toast.success('Preview alert fired to live feed');
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
                      }}
                      className="text-white border-white/20 bg-white/5 hover:bg-white/10"
                      data-testid="ps-alert-preview-btn">
                <Radio className="h-4 w-4 mr-2" /> Preview live
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => mut.mutate(form)} disabled={mut.isPending}
                className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="ps-save-btn">
          <Save className="h-4 w-4 mr-2" /> {mut.isPending ? 'Saving...' : 'Save Platform Settings'}
        </Button>
      </div>
    </div>
  );
};
